// Consumes a `?notice=…` query param on mount and fires a warning
// toast with that message, then strips the param from the URL so a
// browser back doesn't re-show it.
//
// This is the bridge that lets RequireRole's redirects ("you don't
// have access to X") surface as a real toast on the destination page
// without the route guards needing direct access to the toast API.
//
// Mounted once inside PageLayout so every authenticated page
// participates. Public pages (/sign-in, /accept-invite) handle their
// own messaging via the existing `state.notice` channel.
//
// The pure parsing/cleaning logic lives in
// `notice-from-query-helpers.ts` for unit testing.

import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { processNoticeQuery } from "./notice-from-query-helpers";
import { useToast } from "./Toast";

export function NoticeFromQuery() {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  // Guard against double-firing under React strict mode + against
  // the same notice firing again if the user navigates back.
  const lastFiredRef = useRef<string | null>(null);

  useEffect(() => {
    const result = processNoticeQuery(location.pathname, location.search);
    if (!result.notice) {
      lastFiredRef.current = null;
      return;
    }
    if (lastFiredRef.current === result.fingerprint) return;
    lastFiredRef.current = result.fingerprint;

    toast.warning(result.notice);
    void navigate(result.cleanedUrl, { replace: true });
  }, [location.pathname, location.search, navigate, toast]);

  return null;
}
